import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import TrainingSessionsTable, { EditableSession } from './TrainingSessionsTable'
import withAuthorization from '../../components/HOC/withAuthorization'
import usersApi from '../../api/users'
import trainingplansApi from '../../api/trainingplans'
import {
  TrainingSession,
  EditableInterval,
  ActivityTypes,
} from './TrainingSessionsTable/utils'
import AddSessionModal, { AddSessionData } from './AddSessionModal'
import ConfirmDialog from './ConfirmDialog'

type ConfirmAction =
  | { type: 'save' }
  | { type: 'save-warned'; message: string }
  | { type: 'cancel' }
  | { type: 'delete'; tempId: string }

type HistoryEntry = {
  sessions: EditableSession[]
  intervals: Record<string, EditableInterval[]>
}

function toEditableSessions(sessions: TrainingSession[]): EditableSession[] {
  return sessions
    .map((s) => ({ ...s, tempId: String(s.id) }))
    .sort(
      (a, b) =>
        new Date(String(a.date)).getTime() - new Date(String(b.date)).getTime()
    )
}

function buildInitialIntervals(
  sessions: EditableSession[]
): Record<string, EditableInterval[]> {
  const map: Record<string, EditableInterval[]> = {}
  sessions.forEach((s) => {
    map[s.tempId] = (s.intervals ?? []).map((interval, idx) => ({
      ...interval,
      tempId: `interval_${interval.id}_${idx}`,
      // If pace/distance came from an expression, clear the computed numeric value
      // so the edit input starts empty rather than pre-filled with a calculated number.
      goalPaceMsPerKm: interval.goalPaceExpression
        ? undefined
        : interval.goalPaceMsPerKm,
      goalDistanceMeters: interval.goalDistanceMeters || undefined,
    }))
  })
  return map
}

const TrainingSessions = () => {
  const { userId, planId } = useParams()
  const [data, setData] = useState<TrainingSession[]>([])
  const [unitSystem, setUnitSystem] = useState<string | null>(null)

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false)
  const [editedSessions, setEditedSessions] = useState<EditableSession[]>([])
  const [editedIntervals, setEditedIntervals] = useState<
    Record<string, EditableInterval[]>
  >({})
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [newSessionPayloads, setNewSessionPayloads] = useState<
    Record<string, AddSessionData>
  >({})
  const [planDateRange, setPlanDateRange] = useState<{
    min: string
    max: string
  } | null>(null)

  type PlanVersion = {
    id: number
    planId: number
    version: number
    coachId?: number
    createdAt: string
  }
  type ChangelogEntry = {
    id: number
    sessionTitle: string
    action: 'created' | 'updated' | 'deleted'
  }
  const [planVersions, setPlanVersions] = useState<PlanVersion[]>([])
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(
    null
  )
  const [changelog, setChangelog] = useState<ChangelogEntry[]>([])
  const [changelogLoading, setChangelogLoading] = useState(false)

  const handleVersionClick = (versionId: number) => {
    if (selectedVersionId === versionId) {
      setSelectedVersionId(null)
      setChangelog([])
      return
    }
    setSelectedVersionId(versionId)
    setChangelog([])
    setChangelogLoading(true)
    trainingplansApi
      .getVersionChangelog(userId!, planId!, versionId)
      .then((res) => setChangelog(res?.data ?? []))
      .catch(() => setChangelog([]))
      .finally(() => setChangelogLoading(false))
  }

  // Refs to capture current state for history without stale closures
  const sessionsRef = useRef<EditableSession[]>([])
  const intervalsRef = useRef<Record<string, EditableInterval[]>>({})
  const planDateRangeRef = useRef<{ min: string; max: string } | null>(null)
  useEffect(() => {
    sessionsRef.current = editedSessions
  }, [editedSessions])
  useEffect(() => {
    intervalsRef.current = editedIntervals
  }, [editedIntervals])
  useEffect(() => {
    planDateRangeRef.current = planDateRange
  }, [planDateRange])

  // Field tracking refs to group character-by-character edits into one undo step
  const editingSessionFieldRef = useRef<{
    tempId: string
    field: string
  } | null>(null)
  const editingIntervalFieldRef = useRef<{
    tempId: string
    field: string
  } | null>(null)

  useEffect(() => {
    if (!userId) return
    usersApi
      .getUserPreferences(userId)
      .then((res) => setUnitSystem(res?.data?.unitSystem ?? null))
      .catch(() => {})
  }, [userId])

  useEffect(() => {
    if (!userId || !planId) return
    trainingplansApi
      .getUserTrainingPlans(Number(userId))
      .then((res) => {
        const plan = res?.data?.find(
          (p: { id: number; startDate: string; targetDate: string }) =>
            String(p.id) === String(planId)
        )
        if (plan?.startDate && plan?.targetDate) {
          setPlanDateRange({
            min: String(plan.startDate).substring(0, 10),
            max: String(plan.targetDate).substring(0, 10),
          })
        }
      })
      .catch(() => {})
  }, [userId, planId])

  useEffect(() => {
    if (!userId || !planId) return
    trainingplansApi
      .getPlanVersions(userId, planId)
      .then((res) => setPlanVersions(res?.data ?? []))
      .catch(() => {})
  }, [userId, planId])

  const fetchSessions = useCallback(async () => {
    try {
      const response = await usersApi.getUserPlan({
        userId,
        planId,
        source: 'database',
      })
      setData(
        response?.data?.map((s: TrainingSession) => ({
          ...s,
          subRow: [...(s?.intervals ?? [])],
        }))
      )
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    }
  }, [userId, planId])

  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  // ─── History ────────────────────────────────────────────────────────────

  const pushHistory = useCallback(() => {
    setHistory((h) => [
      ...h,
      { sessions: sessionsRef.current, intervals: intervalsRef.current },
    ])
  }, [])

  // ─── Edit mode actions ───────────────────────────────────────────────────

  const enterEditMode = () => {
    const initialSessions = toEditableSessions(data)
    setEditedSessions(initialSessions)
    setEditedIntervals(buildInitialIntervals(initialSessions))
    setHistory([])
    setSaveError(null)
    setNewSessionPayloads({})
    editingSessionFieldRef.current = null
    editingIntervalFieldRef.current = null
    setIsEditMode(true)
  }

  const handleSessionChange = useCallback(
    (tempId: string, field: string, value: string) => {
      if (field === 'date' && value) {
        const range = planDateRangeRef.current
        if (range && (value < range.min || value > range.max)) return
      }
      const editing = editingSessionFieldRef.current
      if (!editing || editing.tempId !== tempId || editing.field !== field) {
        editingSessionFieldRef.current = { tempId, field }
        pushHistory()
      }
      setEditedSessions((prev) => {
        const updated = prev.map((s) =>
          s.tempId === tempId ? { ...s, [field]: value } : s
        )
        return updated.sort(
          (a, b) =>
            new Date(String(a.date)).getTime() -
            new Date(String(b.date)).getTime()
        )
      })
    },
    [pushHistory]
  )

  const handleSessionBlur = useCallback(() => {
    editingSessionFieldRef.current = null
  }, [])

  const handleDeleteSessionRequest = useCallback((tempId: string) => {
    setConfirmAction({ type: 'delete', tempId })
  }, [])

  const executeDelete = (tempId: string) => {
    pushHistory()
    setEditedSessions((prev) => prev.filter((s) => s.tempId !== tempId))
    setEditedIntervals((prev) => {
      const next = { ...prev }
      delete next[tempId]
      return next
    })
  }

  const handleUndo = () => {
    if (history.length === 0) return
    const prev = history[history.length - 1]
    setHistory((h) => h.slice(0, -1))
    setEditedSessions(prev.sessions)
    setEditedIntervals(prev.intervals)
    editingSessionFieldRef.current = null
    editingIntervalFieldRef.current = null
  }

  const handleAddSession = (sessionData: AddSessionData) => {
    const tempId = `new_${Date.now()}_${Math.random()}`
    const newSession: EditableSession = {
      tempId,
      isNew: true,
      id: -1,
      title: sessionData.title,
      activityType: sessionData.activityType as ActivityTypes,
      date: sessionData.date as unknown as Date,
      completed: false,
      week: 0,
      day: 0,
      trainingPlanId: Number(planId),
      createdAt: new Date(),
      updatedAt: new Date(),
      intervals: [],
    }
    const now = new Date()
    const defaultSplit: EditableInterval = {
      tempId: `new_split_${Date.now()}`,
      isNew: true,
      id: -1,
      intervalNo: 1,
      title: sessionData.title,
      goalDistanceMeters: sessionData.goalDistanceMeters ?? undefined,
      goalPaceMsPerKm: sessionData.goalPaceMsPerKm ?? undefined,
      goalTimeMs: sessionData.goalTimeMs ?? undefined,
      trainingSessionId: -1,
      createdAt: now,
      updatedAt: now,
    }
    pushHistory()
    setEditedSessions((prev) =>
      [...prev, newSession].sort(
        (a, b) =>
          new Date(String(a.date)).getTime() -
          new Date(String(b.date)).getTime()
      )
    )
    setEditedIntervals((prev) => ({ ...prev, [tempId]: [defaultSplit] }))
    setNewSessionPayloads((p) => ({ ...p, [tempId]: sessionData }))
    setShowAddModal(false)
  }

  // ─── Interval actions ────────────────────────────────────────────────────

  const handleIntervalChange = useCallback(
    (
      sessionTempId: string,
      intervalTempId: string,
      field: string,
      value: unknown
    ) => {
      const editing = editingIntervalFieldRef.current
      if (
        !editing ||
        editing.tempId !== intervalTempId ||
        editing.field !== field
      ) {
        editingIntervalFieldRef.current = { tempId: intervalTempId, field }
        pushHistory()
      }
      setEditedIntervals((prev) => ({
        ...prev,
        [sessionTempId]: (prev[sessionTempId] ?? []).map((i) =>
          i.tempId === intervalTempId ? { ...i, [field]: value } : i
        ),
      }))
    },
    [pushHistory]
  )

  const handleIntervalBlur = useCallback(() => {
    editingIntervalFieldRef.current = null
  }, [])

  const handleAddInterval = useCallback(
    (sessionTempId: string) => {
      pushHistory()
      setEditedIntervals((prev) => {
        const existing = prev[sessionTempId] ?? []
        const newInterval: EditableInterval = {
          tempId: `interval_new_${Date.now()}_${Math.random()}`,
          isNew: true,
          id: -1,
          title: '',
          intervalNo: existing.length + 1,
          trainingSessionId: -1,
          goalPaceExpression: '',
          createdAt: new Date(),
          updatedAt: new Date(),
        }
        return { ...prev, [sessionTempId]: [...existing, newInterval] }
      })
    },
    [pushHistory]
  )

  const handleDeleteInterval = useCallback(
    (sessionTempId: string, intervalTempId: string) => {
      pushHistory()
      setEditedIntervals((prev) => {
        const filtered = (prev[sessionTempId] ?? []).filter(
          (i) => i.tempId !== intervalTempId
        )
        const renumbered = filtered.map((i, idx) => ({
          ...i,
          intervalNo: idx + 1,
        }))
        return { ...prev, [sessionTempId]: renumbered }
      })
    },
    [pushHistory]
  )

  const handleDuplicateInterval = useCallback(
    (sessionTempId: string, intervalTempId: string) => {
      pushHistory()
      setEditedIntervals((prev) => {
        const intervals = prev[sessionTempId] ?? []
        const idx = intervals.findIndex((i) => i.tempId === intervalTempId)
        if (idx === -1) return prev
        const duplicate: EditableInterval = {
          ...intervals[idx],
          tempId: `interval_new_${Date.now()}_${Math.random()}`,
          id: -1,
          isNew: true,
        }
        const next = [
          ...intervals.slice(0, idx + 1),
          duplicate,
          ...intervals.slice(idx + 1),
        ].map((i, i_idx) => ({ ...i, intervalNo: i_idx + 1 }))
        return { ...prev, [sessionTempId]: next }
      })
    },
    [pushHistory]
  )

  const handleReorderIntervals = useCallback(
    (sessionTempId: string, fromIndex: number, toIndex: number) => {
      pushHistory()
      setEditedIntervals((prev) => {
        const intervals = [...(prev[sessionTempId] ?? [])]
        const [removed] = intervals.splice(fromIndex, 1)
        intervals.splice(toIndex, 0, removed)
        const renumbered = intervals.map((i, idx) => ({
          ...i,
          intervalNo: idx + 1,
        }))
        return { ...prev, [sessionTempId]: renumbered }
      })
    },
    [pushHistory]
  )

  // ─── Change detection ────────────────────────────────────────────────────

  const hasChanges = (() => {
    if (!isEditMode) return false

    // Session-level changes
    const originalIds = new Set(data.map((s) => s.id))
    const editedIds = new Set(
      editedSessions.filter((s) => !s.isNew).map((s) => s.id)
    )
    if (editedSessions.some((s) => s.isNew)) return true
    if (Array.from(originalIds).some((id) => !editedIds.has(id))) return true
    const sessionChanged = editedSessions.some((s) => {
      const original = data.find((o) => o.id === s.id)
      if (!original) return false
      return (
        original.title !== s.title ||
        String(original.date).substring(0, 10) !==
          String(s.date).substring(0, 10) ||
        original.activityType !== s.activityType
      )
    })
    if (sessionChanged) return true

    // Interval-level changes
    return Object.entries(editedIntervals).some(
      ([sessionTempId, intervals]) => {
        const session = editedSessions.find((s) => s.tempId === sessionTempId)
        if (!session || session.isNew) return false
        const originalSession = data.find(
          (d) => String(d.id) === String(session.id)
        )
        if (!originalSession) return true
        const origIntervals = originalSession.intervals ?? []
        if (intervals.length !== origIntervals.length) return true
        return intervals.some((i) => {
          if (i.isNew) return true
          const orig = origIntervals.find((o) => o.id === i.id)
          if (!orig) return true
          const origNormalizedPace = orig.goalPaceExpression
            ? undefined
            : orig.goalPaceMsPerKm
          const origNormalizedDistance = orig.goalDistanceMeters || undefined
          return (
            orig.title !== i.title ||
            orig.goalTimeMs !== i.goalTimeMs ||
            origNormalizedDistance !== i.goalDistanceMeters ||
            origNormalizedPace !== i.goalPaceMsPerKm ||
            orig.goalPaceExpression !== i.goalPaceExpression ||
            orig.intervalNo !== i.intervalNo
          )
        })
      }
    )
  })()

  const didIntervalsChangeForSession = useCallback(
    (sessionTempId: string) => {
      const session = editedSessions.find((s) => s.tempId === sessionTempId)
      if (!session) return false
      if (session.isNew) return true

      const originalSession = data.find((d) => d.id === session.id)
      if (!originalSession) return true

      const intervals = editedIntervals[sessionTempId] ?? []
      const origIntervals = originalSession.intervals ?? []
      if (intervals.length !== origIntervals.length) return true

      return intervals.some((i) => {
        if (i.isNew) return true
        const orig = origIntervals.find((o) => o.id === i.id)
        if (!orig) return true
        const origNormalizedPace = orig.goalPaceExpression
          ? undefined
          : orig.goalPaceMsPerKm
        const origNormalizedDistance = orig.goalDistanceMeters || undefined
        return (
          orig.title !== i.title ||
          orig.goalTimeMs !== i.goalTimeMs ||
          origNormalizedDistance !== i.goalDistanceMeters ||
          origNormalizedPace !== i.goalPaceMsPerKm ||
          orig.goalPaceExpression !== i.goalPaceExpression ||
          orig.intervalNo !== i.intervalNo
        )
      })
    },
    [data, editedIntervals, editedSessions]
  )

  const hasIntervalValidationErrors = (() => {
    if (!isEditMode) return false
    return editedSessions.some((session) => {
      if (!didIntervalsChangeForSession(session.tempId)) return false
      const intervals = editedIntervals[session.tempId] ?? []
      return intervals.some((interval) => {
        const hasTitle = !!interval.title?.trim()
        const hasDistance =
          interval.goalDistanceMeters != null &&
          Number(interval.goalDistanceMeters) > 0
        const hasTime =
          interval.goalTimeMs != null && Number(interval.goalTimeMs) > 0
        const hasPace =
          (interval.goalPaceMsPerKm != null &&
            Number(interval.goalPaceMsPerKm) > 0) ||
          !!interval.goalPaceExpression?.trim()

        if (!hasTitle) return true
        if (!hasPace) return true
        if ((hasDistance && hasTime) || (!hasDistance && !hasTime)) return true
        return false
      })
    })
  })()

  // ─── Validation ──────────────────────────────────────────────────────────

  const getIntervalHardError = (): string | null => {
    let error: string | null = null
    editedSessions.some((session) => {
      if (!didIntervalsChangeForSession(session.tempId)) return false
      const intervals = editedIntervals[session.tempId] ?? []
      return intervals.some((interval) => {
        if (!interval.title?.trim()) {
          error = `A split in session "${session.title}" has an empty title.`
          return true
        }
        const hasDistance =
          interval.goalDistanceMeters != null &&
          Number(interval.goalDistanceMeters) > 0
        const hasTime =
          interval.goalTimeMs != null && Number(interval.goalTimeMs) > 0
        const hasPace =
          (interval.goalPaceMsPerKm != null &&
            Number(interval.goalPaceMsPerKm) > 0) ||
          !!interval.goalPaceExpression?.trim()

        if (!hasPace) {
          error = `Split "${interval.title || 'untitled'}" in "${
            session.title
          }" must have Goal pace (or a pace expression).`
          return true
        }

        if ((hasDistance && hasTime) || (!hasDistance && !hasTime)) {
          error = `Split "${interval.title || 'untitled'}" in "${
            session.title
          }" must have exactly one of: Distance selection (Goal distance) OR Goal time.`
          return true
        }
        return false
      })
    })
    return error
  }

  const getIntervalWarning = (): string | null => {
    let warning: string | null = null
    editedSessions.some((session) => {
      const intervals = editedIntervals[session.tempId] ?? []
      if (
        session.activityType !== ActivityTypes.intervalSession ||
        intervals.length > 1
      )
        return false

      // Only warn for new sessions or sessions whose intervals were actually changed
      const wasEdited = (() => {
        if (session.isNew) return true
        const originalSession = data.find((d) => d.id === session.id)
        if (!originalSession) return true
        const origIntervals = originalSession.intervals ?? []
        if (intervals.length !== origIntervals.length) return true
        return intervals.some((i) => {
          if (i.isNew) return true
          const orig = origIntervals.find((o) => o.id === i.id)
          if (!orig) return true
          const origNormalizedPace = orig.goalPaceExpression
            ? undefined
            : orig.goalPaceMsPerKm
          const origNormalizedDistance = orig.goalDistanceMeters || undefined
          return (
            orig.title !== i.title ||
            orig.goalTimeMs !== i.goalTimeMs ||
            origNormalizedDistance !== i.goalDistanceMeters ||
            origNormalizedPace !== i.goalPaceMsPerKm ||
            orig.goalPaceExpression !== i.goalPaceExpression ||
            orig.intervalNo !== i.intervalNo
          )
        })
      })()

      if (wasEdited) {
        warning = `Interval session "${session.title}" only has ${intervals.length} split. Are you sure you want to save?`
        return true
      }
      return false
    })
    return warning
  }

  // ─── Save ────────────────────────────────────────────────────────────────

  const handleSave = async (skipWarnings = false) => {
    const hardError = getIntervalHardError()
    if (hardError) {
      setSaveError(hardError)
      return
    }
    if (!skipWarnings) {
      const warning = getIntervalWarning()
      if (warning) {
        setConfirmAction({ type: 'save-warned', message: warning })
        return
      }
    }

    setIsSaving(true)
    setSaveError(null)
    try {
      const originalIds = new Set(data.map((s) => s.id))
      const editedIds = new Set(
        editedSessions.filter((s) => !s.isNew).map((s) => s.id)
      )

      const deleted = Array.from(originalIds).filter((id) => !editedIds.has(id))

      const modified = editedSessions
        .filter((s) => {
          if (s.isNew) return false
          const original = data.find((o) => String(o.id) === String(s.id))
          if (!original) return false
          return (
            original.title !== s.title ||
            String(original.date).substring(0, 10) !==
              String(s.date).substring(0, 10) ||
            original.activityType !== s.activityType
          )
        })
        .map((s) => ({
          id: s.id,
          title: s.title,
          date: String(s.date).substring(0, 10),
          activityType: s.activityType as string,
          sportType: (s as { sportType?: string }).sportType ?? '',
        }))

      const added = editedSessions
        .filter((s) => s.isNew && !!newSessionPayloads[s.tempId])
        .map((s) => ({
          // Start from the original modal payload (e.g. measurements),
          // but always prefer the currently edited session row fields.
          ...newSessionPayloads[s.tempId],
          title: s.title,
          activityType: s.activityType as string,
          date: String(s.date).substring(0, 10),
          intervals: (editedIntervals[s.tempId] ?? []).map((i) => ({
            title: i.title,
            intervalNo: i.intervalNo,
            goalTimeMs: i.goalTimeMs ?? null,
            goalDistanceMeters: i.goalDistanceMeters ?? null,
            goalPaceMsPerKm: i.goalPaceMsPerKm ?? null,
            goalPaceExpression: i.goalPaceExpression ?? null,
          })),
        }))

      // Build interval changes for existing sessions
      const intervalChanges = Object.entries(editedIntervals)
        .map(([sessionTempId, intervals]) => {
          const session = editedSessions.find((s) => s.tempId === sessionTempId)
          if (!session || session.isNew) return null
          const sessionId = session.id
          const originalSession = data.find((d) => d.id === sessionId)
          if (!originalSession) return null

          const origIntervalIds = new Set(
            (originalSession.intervals ?? []).map((i) => i.id)
          )
          const currentIntervalIds = new Set(
            intervals.filter((i) => !i.isNew).map((i) => i.id)
          )

          const deletedIntervals = Array.from(origIntervalIds).filter(
            (id) => !currentIntervalIds.has(id)
          )

          const modifiedIntervals = intervals
            .filter((i) => !i.isNew)
            .filter((i) => {
              const orig = (originalSession.intervals ?? []).find(
                (o) => o.id === i.id
              )
              if (!orig) return false
              // Apply same normalisation as buildInitialIntervals so unchanged
              // intervals with a goalPaceExpression aren't falsely flagged
              const origPace = orig.goalPaceExpression
                ? undefined
                : orig.goalPaceMsPerKm
              const origDistance = orig.goalDistanceMeters || undefined
              return (
                orig.title !== i.title ||
                orig.goalTimeMs !== i.goalTimeMs ||
                origDistance !== i.goalDistanceMeters ||
                origPace !== i.goalPaceMsPerKm ||
                orig.goalPaceExpression !== i.goalPaceExpression ||
                orig.intervalNo !== i.intervalNo
              )
            })
            .map((i) => ({
              id: i.id,
              title: i.title,
              intervalNo: i.intervalNo,
              goalTimeMs: i.goalTimeMs ?? null,
              goalDistanceMeters: i.goalDistanceMeters ?? null,
              goalPaceMsPerKm: i.goalPaceMsPerKm ?? null,
              goalPaceExpression: i.goalPaceExpression ?? null,
            }))

          const addedIntervals = intervals
            .filter((i) => i.isNew)
            .map((i) => ({
              title: i.title,
              intervalNo: i.intervalNo,
              goalTimeMs: i.goalTimeMs ?? null,
              goalDistanceMeters: i.goalDistanceMeters ?? null,
              goalPaceMsPerKm: i.goalPaceMsPerKm ?? null,
              goalPaceExpression: i.goalPaceExpression ?? null,
            }))

          if (
            !deletedIntervals.length &&
            !modifiedIntervals.length &&
            !addedIntervals.length
          )
            return null
          return {
            sessionId,
            deleted: deletedIntervals,
            modified: modifiedIntervals,
            added: addedIntervals,
          }
        })
        .filter(
          (
            c
          ): c is {
            sessionId: number
            deleted: number[]
            modified: {
              id: number
              title: string
              intervalNo: number
              goalTimeMs: number | null
              goalDistanceMeters: number | null
              goalPaceMsPerKm: number | null
              goalPaceExpression: string | null
            }[]
            added: {
              title: string
              intervalNo: number
              goalTimeMs: number | null
              goalDistanceMeters: number | null
              goalPaceMsPerKm: number | null
              goalPaceExpression: string | null
            }[]
          } => c !== null
        )

      await trainingplansApi.batchUpdateSessions(userId!, planId!, {
        deleted,
        modified,
        added,
        intervalChanges,
      })

      await fetchSessions()
      trainingplansApi
        .getPlanVersions(userId!, planId!)
        .then((res) => setPlanVersions(res?.data ?? []))
        .catch(() => {})
      setIsEditMode(false)
      setEditedSessions([])
      setEditedIntervals({})
      setHistory([])
      setNewSessionPayloads({})
    } catch (err) {
      const e = err as { response?: { data?: unknown }; message?: string }
      const errData = e?.response?.data
      let message = 'Save failed'
      if (typeof errData === 'string') {
        try {
          const parsed = JSON.parse(errData) as Array<{ message: string }>
          if (Array.isArray(parsed) && parsed.length > 0) {
            message = parsed.map((d) => d.message).join(', ')
          }
        } catch {
          message = errData || message
        }
      } else if (
        errData &&
        typeof errData === 'object' &&
        'message' in errData
      ) {
        message = (errData as { message: string }).message
      } else if (e?.message) {
        message = e.message
      }
      setSaveError(message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancelEdit = () => {
    setIsEditMode(false)
    setEditedSessions([])
    setEditedIntervals({})
    setHistory([])
    setNewSessionPayloads({})
    setSaveError(null)
    editingSessionFieldRef.current = null
    editingIntervalFieldRef.current = null
  }

  const handleConfirm = () => {
    if (!confirmAction) return
    const action = confirmAction
    setConfirmAction(null)
    if (action.type === 'save') {
      handleSave()
    } else if (action.type === 'save-warned') {
      handleSave(true)
    } else if (action.type === 'cancel') {
      handleCancelEdit()
    } else if (action.type === 'delete') {
      executeDelete(action.tempId)
    }
  }

  const confirmMessages: Record<string, string> = {
    save: 'You are about to update a plan with edits. Are you sure you would like to continue?',
    'save-warned':
      confirmAction?.type === 'save-warned' ? confirmAction.message : '',
    cancel:
      'Are you sure you want to cancel all changes? All edits will be lost.',
    delete: 'Are you sure you want to delete this session?',
  }

  return (
    <div>
      {/* Sticky header: edit mode banner + unit system bar */}
      <div className="sticky top-0 z-20">
        {isEditMode && (
          <div className="bg-red-400 text-white text-center font-bold py-2 text-sm tracking-widest">
            EDIT MODE / EDIT MODE / EDIT MODE
          </div>
        )}
        {unitSystem && (
          <div className="bg-blue-600 text-white text-center py-2 text-sm font-semibold tracking-wide shadow-sm">
            Unit preference:{' '}
            <span className="font-extrabold uppercase tracking-widest">
              {unitSystem === 'imperial' ? 'Miles' : 'Kilometres'}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4 px-4 pt-4">
        <div className="w-1/3" />

        <div className="flex flex-col items-end gap-2">
          {planVersions.length > 0 && !isEditMode && (
            <div className="text-xs text-right space-y-1 mb-1 min-w-[260px]">
              {planVersions.map((v) => (
                <div key={v.id}>
                  <button
                    type="button"
                    onClick={() => handleVersionClick(v.id)}
                    className="w-full text-right hover:text-blue-600 transition-colors"
                  >
                    <span
                      className={`font-semibold ${
                        selectedVersionId === v.id
                          ? 'text-blue-600'
                          : 'text-gray-500'
                      }`}
                    >
                      V{v.version}
                    </span>
                    <span className="text-gray-400">
                      {' — '}
                      {new Date(v.createdAt).toLocaleString()}
                    </span>
                    <span className="ml-1 text-gray-300">
                      {selectedVersionId === v.id ? '▲' : '▼'}
                    </span>
                  </button>
                  {selectedVersionId === v.id && (
                    <div className="mt-1 mb-1 text-left border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden">
                      {(() => {
                        if (changelogLoading) {
                          return (
                            <p className="px-3 py-2 text-gray-400 text-xs">
                              Loading…
                            </p>
                          )
                        }

                        if (changelog.length === 0) {
                          return (
                            <p className="px-3 py-2 text-gray-400 text-xs italic">
                              No session changes recorded.
                            </p>
                          )
                        }

                        const actionClass: Record<string, string> = {
                          created: 'bg-green-100 text-green-700',
                          deleted: 'bg-red-100 text-red-700',
                          updated: 'bg-blue-100 text-blue-700',
                        }

                        return (
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="px-3 py-1.5 text-left font-semibold text-gray-600">
                                  Session
                                </th>
                                <th className="px-3 py-1.5 text-left font-semibold text-gray-600">
                                  Change
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {changelog.map((entry) => (
                                <tr
                                  key={entry.id}
                                  className="border-t border-gray-100"
                                >
                                  <td className="px-3 py-1.5 text-gray-700">
                                    {entry.sessionTitle}
                                  </td>
                                  <td className="px-3 py-1.5">
                                    <span
                                      className={`inline-block px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide text-[10px] ${
                                        actionClass[entry.action] ??
                                        actionClass.updated
                                      }`}
                                    >
                                      {entry.action}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )
                      })()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {!isEditMode ? (
            <button
              type="button"
              onClick={enterEditMode}
              className="px-5 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
            >
              Edit
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                className="px-5 py-2 text-sm font-medium rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors shadow-sm"
              >
                Add sessions
              </button>
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setConfirmAction({ type: 'save' })}
                  disabled={
                    isSaving || !hasChanges || hasIntervalValidationErrors
                  }
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors shadow-sm"
                >
                  {isSaving ? 'Saving...' : 'Save changes'}
                </button>
                <button
                  type="button"
                  onClick={handleUndo}
                  disabled={history.length === 0}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition-colors shadow-sm"
                >
                  Undo
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmAction({ type: 'cancel' })}
                  disabled={!hasChanges}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-red-400 text-white hover:bg-red-500 disabled:opacity-50 transition-colors shadow-sm"
                >
                  Cancel all changes
                </button>
                <button
                  type="button"
                  onClick={() =>
                    hasChanges
                      ? setConfirmAction({ type: 'cancel' })
                      : handleCancelEdit()
                  }
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                >
                  Exit edit mode
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {saveError && (
        <div className="mx-4 mt-3 px-4 py-2 bg-red-100 border border-red-300 text-red-700 text-sm rounded-lg">
          {saveError}
        </div>
      )}

      <TrainingSessionsTable
        key={isEditMode ? 'edit' : 'view'}
        sessions={data}
        isEditMode={isEditMode}
        editedSessions={editedSessions}
        onSessionChange={handleSessionChange}
        onSessionBlur={handleSessionBlur}
        onDeleteSessionRequest={handleDeleteSessionRequest}
        planDateMin={planDateRange?.min}
        planDateMax={planDateRange?.max}
        editedIntervals={editedIntervals}
        unitSystem={unitSystem}
        onIntervalChange={handleIntervalChange}
        onIntervalBlur={handleIntervalBlur}
        onAddInterval={handleAddInterval}
        onDeleteInterval={handleDeleteInterval}
        onDuplicateInterval={handleDuplicateInterval}
        onReorderIntervals={handleReorderIntervals}
      />

      {showAddModal && (
        <AddSessionModal
          onAdd={handleAddSession}
          onClose={() => setShowAddModal(false)}
          minDate={planDateRange?.min}
          maxDate={planDateRange?.max}
          unitSystem={unitSystem}
        />
      )}

      {confirmAction && (
        <ConfirmDialog
          message={confirmMessages[confirmAction.type]}
          onConfirm={handleConfirm}
          onCancel={() => setConfirmAction(null)}
          danger={confirmAction.type === 'delete'}
        />
      )}
    </div>
  )
}

export default withAuthorization(TrainingSessions)
